import { Tabs as AntTabs, type TabsProps } from 'antd';
import React, { useMemo, useState } from 'react';

import type { TabItemProps } from './Tabs.types';
import TabsItem from './TabsItem';

type Props = {
  className?: string;
  children: React.ReactNode;
  type?: NonNullable<TabsProps['type']>;
  activeKey?: string;
  onChange?: (key: string) => void;
};

const Tabs = ({
  className,
  children,
  type,
  activeKey: controlledActiveKey,
  onChange
}: Props) => {
  const [uncontrolledActiveKey, setUncontrolledActiveKey] = useState<
    string | null
  >(null);

  const isControlled = controlledActiveKey !== undefined;
  const activeKey = isControlled ? controlledActiveKey : uncontrolledActiveKey;

  const items = useMemo(
    () =>
      React.Children.toArray(children)
        .filter(Boolean)
        .filter(React.isValidElement)
        .filter(
          ({ type }) =>
            (type as React.ComponentType).displayName === 'Tabs.Item'
        )
        .map(child => {
          const {
            keyProp,
            label,
            children: itemChildren,
            isDefault,
            disabled
          } = child.props as TabItemProps;

          const key =
            keyProp ??
            (typeof label === 'string'
              ? label
              : (() => {
                  throw new Error(
                    'Tabs.Item: when label is not a string, keyProp is required'
                  );
                })());
          return {
            key,
            label,
            children: itemChildren,
            isDefault,
            disabled
          };
        }),
    [children]
  );

  const defaultActiveKey = useMemo(
    () => items.find(item => item.isDefault)?.key ?? items[0]?.key ?? undefined,
    [items]
  );

  const finalActiveKey = activeKey ?? defaultActiveKey ?? undefined;

  const handleChange = (key: string) => {
    if (!isControlled) {
      setUncontrolledActiveKey(key);
    }
    onChange?.(key);
  };

  return (
    <AntTabs
      className={className}
      type={type}
      activeKey={finalActiveKey}
      onChange={handleChange}
      items={items}
    />
  );
};

Tabs.Item = TabsItem;

export default Tabs;
